import { IconButton, InputAdornment } from "@material-ui/core";
import Container from "@material-ui/core/Container";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import SendIcon from "@material-ui/icons/Send";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import logo from "../../assets/images/logo_full.svg";
import biLogo from "../../assets/images/logo_bi_small.svg";
import logoBIBlue from "../../assets/images/logo_sbs_blue.svg";
import logoRed from "../../assets/images/logo_small.svg";
import { CButton } from "../../components/buttons";
import { InputWarning } from "../../components/form/warnings";
import { LOCALSTORAGEFIELDS, PAGES } from "../../constants";
import {
  appState,
  setAuth,
  setUserData,
} from "../../redux/reducers/appReducer";
import { getSystemCode, updateFavicon } from "../../services";
import cookiesService from "../../services/cookiesService";
import { getFingerKey } from "../../utils";
import { useHttp } from "../../utils/hooks/http.hook";
import { useCustomSnackbar } from "../../utils/hooks/useCustomSnackbar";
import { sendCodeData } from "./services";
import { LoginBodyType, LoginRes } from "./types";

const cookiesServiceInstance = cookiesService.getService();
export const LogInPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useNavigate();
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
  } = useForm();
  const { request, loading } = useHttp();
  const { addDefaultSnack } = useCustomSnackbar();

  const [showCodeInput, setShowCodeInput] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const { isUserAuthenticated } = useSelector(appState);
  const [system, setSystem] = React.useState(getSystemCode());

  const onSubmit = async (body) => {
    body.finger = getFingerKey();
    try {
      const res = await request<LoginRes>(
        `/auth/login/`,
        "post",
        body,
        {},
        { isTokenRequired: false, throwErrorAll: true }
      );
      if (res) {
        dispatch(setUserData(res.user));
        dispatch(setAuth(true));
        cookiesServiceInstance.setLoginData(res);
        history("/");
      }
    } catch (e) {
      const error = e?.response?.data;
      if (error?.is_device_exist === false) {
        setShowCodeInput(true);
      }
    }
  };

  const sendCode = async () => {
    const body: LoginBodyType = getValues();
    await sendCodeData(body, setCodeLoading, addDefaultSnack);
  };

  const handleSystemChange = (_, newSystem: string) => {
    if (!newSystem) return;
    setSystem(newSystem);
    localStorage.setItem(LOCALSTORAGEFIELDS.activeSystem, newSystem);
    updateFavicon(newSystem as "sr" | "sbs");
  };

  if (isUserAuthenticated) return <Navigate to={PAGES.main} replace={true} />;
  return (
    <Container
      component="main"
      maxWidth="xs"
      style={{ backgroundColor: "#fff", textAlign: "center" }}
    >
      <div className={classes.paper}>
        <div
          style={{
            width: 269,
            marginBottom: 50,
            height: 100,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img alt="" src={system === "sbs" ? logoBIBlue : logo} />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
          <div>
            <Controller
              control={control}
              name="login"
              render={({ field: { onChange, value } }) => (
                <TextField
                  margin="normal"
                  fullWidth
                  type="email"
                  value={value}
                  id="email"
                  label="Email"
                  autoComplete="email"
                  style={{ color: "#818181", fontSize: 18 }}
                  onChange={onChange}
                />
              )}
              rules={{ required: true }}
              defaultValue={""}
            />
            {errors.login && <InputWarning>Это поле обязательно</InputWarning>}
          </div>
          <div>
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value } }) => (
                <TextField
                  margin="normal"
                  fullWidth
                  value={value}
                  id="password"
                  label="Пароль"
                  type={showPassword ? "text" : "password"}
                  style={{ color: "#818181", fontSize: 18 }}
                  onChange={onChange}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              rules={{ required: true }}
              defaultValue={""}
            />
            {errors.password && (
              <InputWarning>Это поле обязательно</InputWarning>
            )}
          </div>
          {showCodeInput && (
            <div>
              <div className={classes.code__wrapper}>
                <div>
                  <Controller
                    control={control}
                    name="code_key"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        margin="normal"
                        fullWidth
                        type="number"
                        value={value}
                        id="email"
                        label="Код"
                        style={{ color: "#818181", fontSize: 18 }}
                        onChange={onChange}
                      />
                    )}
                    rules={{ required: true }}
                    defaultValue={""}
                  />
                </div>
                <CButton
                  type="button"
                  loading={codeLoading}
                  onClick={sendCode}
                  className={classes.code__btn}
                >
                  Получить код <SendIcon className={classes.code__btn_icon} />
                </CButton>
              </div>
              {errors.code_key && (
                <InputWarning nowrap>Это поле обязательно</InputWarning>
              )}
            </div>
          )}

          <div
            style={{
              justifyContent: "flex-start",
              marginTop: 15,
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <span>Выберите систему:</span>
            <ToggleButtonGroup
              value={system}
              exclusive
              onChange={handleSystemChange}
              aria-label="text alignment"
              className={classes.toggle}
              color="primary"
            >
              <ToggleButton
                value="sr"
                aria-label="sr"
                className={classes.toggleButton}
              >
                <img src={logoRed} style={{ width: 20 }} />
                Smart Remont
              </ToggleButton>
              <ToggleButton
                value="sbs"
                aria-label="sbs"
                className={classes.toggleButton}
              >
                <img src={biLogo} style={{ width: 20 }} />
                SBS
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          <div className="d-flex flex-column align-items-center justify-content-center">
            <CButton
              type="submit"
              loading={loading}
              disabled={loading}
              className={classes.submit}
              onClick={onSubmit}
            >
              Войти
            </CButton>
            <p className={classes.link}>
              <Link to={PAGES.forgetPassword}>Забыли пароль?</Link>
            </p>
          </div>
        </form>
      </div>
    </Container>
  );
};

export const useStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    height: "100vh",
    backgroundColor: "#fff",
    justifyContent: "center",
  },
  form: {
    width: 380, // Fix IE 11 issue.
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: theme.spacing(1),
    height: "auto !important",

    [theme.breakpoints.down(450)]: {
      width: "100%",
    },
  },
  submit: {
    margin: "30px 0",
    background: "linear-gradient(104.82deg, #0C4B86 -17.14%, #E42734 83.14%)",
    height: 50,
    fontSize: 18,
    borderRadius: "0.5rem",
    width: "100%",
  },
  code__wrapper: {
    display: "grid",
    alignItems: "flex-end",
    gridTemplateColumns: "5fr 4fr",
    gap: "30px",
  },
  code__btn: {
    background: "#2582d1 !important",
    color: "#fff !important",
    fontSize: "12px !important",
    display: "flex",
    alignItems: "center",
    padding: "5px",
    height: "auto",
    width: "auto",
    marginBottom: "8px !important",
  },
  code__btn_icon: {
    fontSize: "15px !important",
    marginLeft: 5,
  },
  link: {
    textAlign: "center",
    "& a": {
      color: "#0c4388",
      fontSize: 16,
      "&:hover": {
        textDecoration: "none",
      },
    },
  },
  toggle: {
    marginTop: 5,
    width: "100%",
    display: "grid",
    gridTemplateColumns: "6fr 6fr",

    "& .MuiToggleButton-root": {
      padding: "5px 0",
      borderRadius: "0 4px 4px 0",
      fontWeight: 600,
      color: "#404040",

      "&:first-child": {
        borderRadius: "4px 0 0 4px",
      },
    },

    "& .Mui-selected": {
      backgroundColor: "#1976d2 !important",
      color: "#fff",
    },
  },
  toggleButton: {
    "& > span": {
      gap: 5,
    },
  },
}));
